/**
 * Hook de permisos granulares por sub-modulo.
 * Soporta notacion dot (ej: 'sales.orders') con fallback al modulo padre ('sales').
 * La accion 'view_own' otorga acceso 'view' (el filtrado de datos se hace en el backend).
 */
import { useAuthStore } from '@/store/authStore'

export function usePermission() {
  const { activeTenant, user } = useAuthStore()

  /**
   * Verifica si el usuario tiene permiso para una accion en un modulo/sub-modulo.
   * Ejemplos: can('sales.orders', 'create'), can('accounting.vouchers', 'approve')
   */
  const can = (module: string, action: string): boolean => {
    if (user?.is_platform_admin) return true
    const perms = activeTenant?.permissions ?? {}
    // 1. Match exacto
    if (perms[module]?.includes(action)) return true
    // 2. Fallback al padre
    const parent = module.includes('.') ? module.split('.')[0] : null
    if (parent && perms[parent]?.includes(action)) return true
    // 3. Fallback a hijos: 'accounting' → busca en cualquier 'accounting.*'
    if (!module.includes('.')) {
      const prefix = module + '.'
      if (Object.keys(perms).some(k => k.startsWith(prefix) && perms[k].includes(action)))
        return true
    }
    // 4. view_own otorga acceso view (solo al mismo módulo, NO se propaga al padre ni hijos)
    if (action === 'view') {
      if (perms[module]?.includes('view_own')) return true
      if (!module.includes('.')) {
        // Para módulos padre sin dot: buscar en hijos directos con view_own
        const prefix = module + '.'
        if (Object.keys(perms).some(k => k.startsWith(prefix) && perms[k].includes('view_own')))
          return true
      }
    }
    return false
  }

  /**
   * True si el usuario tiene view_own pero NO view completo.
   * Usado para mostrar/ocultar filtros y datos propios vs todos.
   */
  const isViewOwn = (module: string): boolean => {
    if (user?.is_platform_admin) return false
    const perms = activeTenant?.permissions ?? {}
    const hasFull = perms[module]?.includes('view')
    const parent = module.includes('.') ? module.split('.')[0] : null
    if (hasFull || (parent && perms[parent]?.includes('view'))) return false
    return (
      perms[module]?.includes('view_own') ||
      (parent ? perms[parent]?.includes('view_own') : false) ||
      false
    )
  }

  /**
   * True si el módulo está habilitado para este tenant.
   * Aplica a todos los usuarios incluyendo platform admins — es preferencia de UI del tenant.
   */
  const isModuleEnabled = (moduleKey: string): boolean => {
    const disabled = activeTenant?.disabled_modules ?? []
    return !disabled.includes(moduleKey)
  }

  return { can, isViewOwn, isModuleEnabled }
}
